import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import List from '../List/List';
import SidebarTab from '../SidebarTab/SidebarTab';
import createFakeEvent from '../../lib/createFakeEvent';

const ResizeController = ({ children, minimized, onResize }) =>
  <div className="core-resize-controller">
    {children && <div className="toolbar" data-testid="sidebar-toolbar">{children}</div>}
    <div className="buttons">
      <Button iconName={minimized ? 'arrow-right' : 'arrow-left'} onClick={onResize} />
    </div>
  </div>;

ResizeController.propTypes = {
  children: PropTypes.node,
  minimized: PropTypes.bool,
  onResize: PropTypes.func,
};

/**
 * `Sidebar` is a container used for sidebars in applications and modules.
 *
 * Although you may add any component to your sidebar it is recommended that you wrap your components in a {@link SidebarTab}
 * container. This will give you greater flexibility and control, as well as a consistent search field across applications.
 *
 * If you add multiple {@link SidebarTab} components to the sidebar, the `Sidebar` will display tabbed icon buttons in the
 * sidebar resize area. The icons will be retrieved from the *icon* or *iconName* prop from each {@link SidebarTab}
 * component.
 *
 * The sidebar can be displayed in a minimized or normal state. The width is controlled by the *minimized* prop. The
 * resize area will be visible if you add more than one {@link SidebarTab} components to a sidebar or when
 * *onResize* is set.
 *
 * You should set *className* to "narrow" if your sidebar only contains simple components such as input fields and
 * buttons. This will make your sidebar more narrow and allow more space for your content.
 *
 * `Sidebar` styling includes styling for custom filter bars of class "filter-bar" as well as styling for {@link List} and
 * {@link Tree} components to make them consistent across applications.
 *
 * @property {node} children The children of this sidebar. You may add any type of component, but it is recommended that you
 * wrap the components in a {@link SidebarTab}.
 * @property {string} className The class name of this component. Will be appended to "core-sidebar". Set to "narrow"
 * if the content of your sidebar is simple - e.g. input fields and buttons only.
 * @property {string} minimized If set to `true`, the sidebar will be minimized. Defaults to `false`.
 * @property {function} onChange A callback that is called whenever the user has selected a tab. The tab index will be
 * passed as `event.target.value`.
 * @property {function} onResize Can be set to `true` if you want the resize functionality, but you want the
 * component to handle the resizing for you. If you want to handle the resize state yourself you should set this prop to a
 * function that will be invoked every time the user toggles the state. The sidebar state will be in the
 * `event.target.value`.
 * @property {number} selected The index of the selected tab. This prop only has effect when your sidebar has more than one
 * {@link SidebarTab} children. Defaults to 0.
 *
 * @see SidebarTab, TagInput
 */

Sidebar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  minimized: PropTypes.bool,
  onChange: PropTypes.func,
  onResize: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  selected: PropTypes.number,
};

Sidebar.defaultProps = { selected: 0 };

export default function Sidebar(props) {
  const {
    children,
    className,
    onChange,
    onResize,
    options,
    minimized: isMinimized,
    selected,
    ...rest
  } = props;

  const [minimized, setMinimized] = useState(isMinimized);

  const handleMinimizedPropChange = () => {
    if (isMinimized !== undefined && isMinimized !== minimized) {
      setMinimized(isMinimized);
    }
  };

  useEffect(() => {
    handleMinimizedPropChange();
  }, [isMinimized]);

  const handleOnchange = (e) => {
    if (onChange) {
      const index = parseInt(e.target.dataset.index, 10);
      onChange(createFakeEvent(index, e));
    }
  };

  const handleOnResize = (e) => {
    const state = !minimized;
    const event = createFakeEvent(state, e);
    setMinimized(state);
    if (typeof onResize === 'function') {
      onResize(event);
    }
  };

  const type =
    children &&
    (children.type || (Array.isArray(children) && children[0].type));
  let tabs = [];
  let content;

  if (type === SidebarTab) {
    const panels = [].concat(children).filter((child) => child);

    content = panels[selected];
    if (panels.length > 1) {
      tabs = panels.map((tab, index) => (
        <Button
          className={index === selected ? 'selected' : undefined}
          data-index={index}
          icon={tab.props.icon}
          iconName={tab.props.iconName}
          key={index}
          onClick={handleOnchange}
        />
      ));
    }
  } else {
    content = <SidebarTab>{children}</SidebarTab>;
  }

  const cls = cx('core-sidebar', className, {
    minimized: minimized,
    resize: onResize,
    tabs: tabs.length > 1,
  });
  return (
    <div {...rest} className={cls}>
      {content}
      {(onResize || tabs.length > 1) && (
        <ResizeController minimized={minimized} onResize={handleOnResize}>
          {tabs.length > 1 && tabs}
        </ResizeController>
      )}
    </div>
  );
};